import { Component, signal, WritableSignal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { marker } from '@eventlyte/common';
import {
	CustomFormControl,
	CustomFormGroup,
	PrimitiveTagListField,
	TagField
} from '@eventlyte/forms';
import {
	ApiErrorList,
	FormErrorsModal,
	RegistrationStepperControls,
	RegistrationStepperProgress,
	ServerErrorsModal
} from '@eventlyte/registration';
import { TranslocoDirective } from '@jsverse/transloco';
import { AppRoutePaths } from '../../app.routes';
import { LocalRegistrationPageBase } from '../../shared/local-registration-page-base';
import { RegistrationGroupPageRecord } from './registration-group-page-record/registration-group-page-record';

@Component({
	selector: 'app-registration-group-page',
	imports: [
		RegistrationStepperControls,
		RegistrationStepperProgress,
		ServerErrorsModal,
		FormErrorsModal,
		TranslocoDirective,
		TagField,
		PrimitiveTagListField,
		RegistrationGroupPageRecord
	],
	templateUrl: './registration-group-page.html'
})
export class RegistrationGroupPage extends LocalRegistrationPageBase {
	protected override currentStepNumber = 3;
	protected totalRecords = signal(0);

	protected updateTotalRecords(value: number | number[] | undefined): void {
		const totalRecords = (Array.isArray(value) ? value[0] : value) ?? 0;
		const currentRecords = this.extraRecords();
		const currentFormGroups = this.formGroups.length;

		this.totalRecords.set(totalRecords);

		if (totalRecords > currentFormGroups) {
			this.formGroups = [
				...this.formGroups,
				...Array.from(
					{ length: totalRecords - currentFormGroups },
					() =>
						new FormGroup({
							salutation: new CustomFormControl('fields.salutation.label', [
								this.validators.required()
							]),
							firstName: new CustomFormControl('fields.firstName.label', [
								this.validators.required(),
								this.validators.unicodeLettersOnly()
							]),
							lastName: new CustomFormControl('fields.lastName.label', [
								this.validators.required(),
								this.validators.unicodeLettersOnly()
							]),
							companyName: new CustomFormControl('fields.companyName.label', [
								this.validators.required()
							]),
							jobTitle: new CustomFormControl('fields.jobTitle.label', [
								this.validators.required()
							]),
							emailAddress: new CustomFormControl('fields.emailAddress.label', [
								this.validators.required(),
								this.validators.emailValidator()
							]),
							confirmEmailAddress: new CustomFormControl(
								'content.fields.confirmEmailAddress.label',
								[
									this.validators.shouldEqualControl(
										'emailAddress',
										marker('content.validation.emailMatch.summary'),
										marker('content.validation.emailMatch.summary')
									),
									this.validators.required(),
									this.validators.emailValidator()
								]
							),
							placeOfResidence: new CustomFormControl(
								'fields.placeOfResidence.label',
								[this.validators.required()]
							),
							mobileNumber: new CustomFormControl('fields.mobileNumber.label', [
								this.validators.minimumLength(8),
								this.validators.phoneNumber(),
								this.validators.required()
							])
						})
				)
			];
		} else if (totalRecords < currentFormGroups) {
			this.formGroups = this.formGroups.slice(0, totalRecords);
		}

		if (totalRecords > currentRecords.length) {
			this.extraRecords.set([
				...currentRecords,
				...Array.from({ length: totalRecords - currentRecords.length }, () =>
					signal({
						mobileNumber: '+852',
						companyName: this.getRecordValue(this.record, 'companyName') || '',
						placeOfResidence: this.getRecordValue(this.record, 'placeOfResidence') || ''
					})
				)
			]);
			return;
		}

		if (totalRecords < currentRecords.length) {
			this.extraRecords.set(currentRecords.slice(0, totalRecords));
		}

		this.validationFormGroups = [new CustomFormGroup({ formGroup: this.formGroups[0] })];
	}

	protected continue(): void {
		super.goToNextStep(this.formGroups, () => {
			this.saving.set(true);

			this.recordStateService.saveRecord({
				...this.recordStateService.getRecord(),
				extraRecords: this.extraRecords().map((x) => x())
			});

			const updateRecordWithResponseValues = (
				record: WritableSignal<Record<string, any>>,
				responseRecord: Record<string, any>
			) => {
				this.setRecordValue(record, 'id', responseRecord['id']);
				this.setRecordValue(record, 'cardNumber', responseRecord['cardNumber']);
			};

			this.registrationService
				.register(this.recordStateService.getRecord(), true, this.currentStepNumber)
				.subscribe({
					next: (record) => {
						record.extraRecords.forEach((extraRecord, index) => {
							updateRecordWithResponseValues(this.extraRecords()[index], extraRecord);
						});

						this.router.navigate([AppRoutePaths.RegistrationConfirmation]);
					},
					error: (apiErrorList: ApiErrorList) => {
						this.serverErrorList.set(apiErrorList);
						this.showServerErrors.set(true);
						this.saving.set(false);
					}
				});
		});
	}
}
